import {Component, OnDestroy, OnInit, Optional, ViewChild} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Apollo, gql } from 'apollo-angular';
import { CoreLibService, FileStore, FsFile, Mutation, Query, UsageStatistics } from 'core-lib';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Table, TableHeaderCheckbox, TableHeaderCheckboxToggleEvent, TableRowSelectEvent } from 'primeng/table';
import { map } from 'rxjs/operators';
import { FilestoreOperationLogComponent } from '../filestore-operation-log/filestore-operation-log.component';
import { FilestoreUploadWebComponent } from '../filestore-upload-web/filestore-upload-web.component';
import { FileStoreUtils } from '../filestore-utils';
import { FilestoreService } from '../filestore.service';

type FileBrowserSortOption = 'date' | 'name' | 'type';

@Component({
  selector: 'lib-filestore-browser',
  templateUrl: './filestore-browser.component.html',
  styleUrl: './filestore-browser.component.scss'
})
export class FilestoreBrowserComponent implements OnInit, OnDestroy {

  @ViewChild('fileTable') table: Table;
  @ViewChild('isAllSelectedCheckbox') isAllSelectedCheckbox: TableHeaderCheckbox;
  @ViewChild('topLevelActionMenu') topLevelActionMenu: any;

  ownerId: string;
  fileStore: FileStore;
  loading = true;
  selectedFiles: FsFile[] = [];
  currentFolderHierachy: MenuItem[];
  usageStatistics: UsageStatistics;
  cuttedFiles: FsFile[];
  pasteButtonLabel: string;
  sortBy: FileBrowserSortOption = 'name';
  enabledSelection = false;
  selectedContextMenuFile: FsFile;
  fileStoreUtils = new FileStoreUtils();
  expandedRowKey: {} = {};
  unoptimizedVideos: FsFile[] = [];
  optimizerTimer: ReturnType<typeof setTimeout>;

  selectionOptions: {
    limit: number,
    mimeTypes: string[]
  };


  bottomActionMenuOpen = false;
  home: MenuItem = {
    title: '', command: (e) => {
      const item = e.item;
      this.changeFolder(null);
    }
  };

  _currentFolder: FsFile;
  searchModeEnabled = false;

  sortOptions: MenuItem[] = [{
    label: $localize`:@@filestore-browser-menu.sort-filename:Sort by Filename`, id: 'sortByFilename', icon: 'fas fa-light fa-check', command: () => {
      this.sortBy = 'name';
      this.sortOptions[0].icon = 'far fa-check';
      this.sortOptions[1].icon = '';
      this.sortOptions[2].icon = '';
    }
  },
  {
    label: $localize`:@@filestore-browser-menu.sort-date:Sort by Date`, id: 'sortByDate', command: () => {
      this.sortBy = 'date';
      this.sortOptions[1].icon = 'far fa-check';
      this.sortOptions[0].icon = '';
      this.sortOptions[2].icon = '';
    }
  }, {
    label: $localize`:@@filestore-browser-menu.sort-contentType:Sort by Type`, id: 'sortByTyoe', command: () => {
      this.sortBy = 'type';
      this.sortOptions[2].icon = 'far fa-check';
      this.sortOptions[1].icon = '';
      this.sortOptions[0].icon = '';
    }
  }];

  contextMenuOptions: MenuItem[] = [
    { label: $localize`:@@filestore-browser-menu.download:Download`, icon: 'far fa-cloud-arrow-down', id: 'download', command: () => { this.bottomActionMenuOpen = false; this.onDownloadFiles(this.selectedContextMenuFile); } },
    { label: $localize`:@@filestore-browser-menu.renameFile:Rename`, icon: 'far fa-pen-field', id: 'rename', command: (event) => { this.bottomActionMenuOpen = false; this.openRenameFileDialog(this.selectedContextMenuFile); } },
    { label: $localize`:@@filestore-browser-menu.deleteFile:Delete`, icon: 'far fa-trash', id: 'delete', command: () => { this.bottomActionMenuOpen = false; this.onDeleteFiles(this.selectedContextMenuFile); } },
    { label: $localize`:@@filestore-browser-menu.cut:Cut`, icon: 'far fa-scissors', id: 'cut', command: () => { this.bottomActionMenuOpen = false; this.onCutFiles(this.selectedContextMenuFile); } },
    { label: 'paste', icon: 'far fa-paste', id: 'paste', command: () => { this.bottomActionMenuOpen = false; this.onPasteFiles(this.selectedContextMenuFile); } }
  ];

  topLevelMenuOptions: MenuItem[] = [
    { label: $localize`:@@filestore-browser-menu.download:Download`, icon: 'far fa-cloud-arrow-down', id: 'download', command: () => { this.onDownloadFiles(); } },
    { label: $localize`:@@filestore-browser-menu.addDirectory:Add directory`, icon: 'far fa-folder-plus', id: 'addDirectory', command: () => { this.openCreateDirectoryDialog(); } },
    { label: $localize`:@@filestore-browser-menu.addFiles:Add files`, icon: 'far fa-file-plus', id: 'addFiles', command: () => { this.openUploadDialog(); } },
    { label: $localize`:@@filestore-browser-menu.renameFile:Rename`, icon: 'far fa-file-pen', id: 'rename', command: () => { this.openRenameFileDialog(); } },
    { label: $localize`:@@filestore-browser-menu.deleteFile:Delete`, icon: 'far fa-trash', id: 'delete', command: () => { this.onDeleteFiles(); } },
    { label: $localize`:@@filestore-browser-menu.cut:Cut`, icon: 'far fa-scissors', id: 'cut', command: () => { this.onCutFiles(); } },
    {
      label: 'paste', icon: 'far fa-paste', id: 'paste', command: () => { this.onPasteFiles(this._currentFolder); }
    },
    {
      label: $localize`:@@filestore-browser-menu.operationLog:Operation Log`, icon: 'far fa-clock-rotate-left', id: 'operation-log', command: () => { this.onOpenOperationLog(); }
    }
  ];

  QUERY_HIERACHY = gql`
  query resolveFileHierarchy($storeId: FSId, $id: FSFileId!){
    resolveFileHierarchy(storeId: $storeId, id: $id){
      id
      name
      downloadUrl
    },
  }`;

  USAGE_STATISTIC_QUERY = gql`
  query usageStatistics{
    usageStatistics{
      storageUsedPercentage
      storageUsedTotal
      storageQuota
    }
  }`;

  USAGE_STATISTIC_MUTATION = gql`
  mutation updateStatistics{
    updateStatistics{
      storageUsedPercentage
      storageUsedTotal
      storageQuota
    }
  }`;

  dialogControl = {
    createDirectory: {
      visible: false,
      name: null
    },
    rename: {
      visible: false,
      file: null,
      fileEnding: ""
    }
  }

  constructor(private apollo: Apollo, private fileStoreService: FilestoreService, private activatedRoute: ActivatedRoute, private messageService: MessageService, private dialogService: DialogService, private coreLibService: CoreLibService, private confirmationService: ConfirmationService, @Optional() private dialogRef: DynamicDialogRef,
    @Optional() private dialogConfig: DynamicDialogConfig) {

  }


  /**
   * get files in current folder
   */
  get filesInCurrentFolder(): FsFile[] {
    if (this.fileStore == null) {
      return [];
    }
    let files: FsFile[] = [];


    if (this.searchModeEnabled == true) {
      files = this.fileStore.files;
    } else {
      if (this._currentFolder == null) {
        files = this.fileStore.files.filter(f => f.parentId == null);
      } else {
        files = this.fileStore.files.filter(f => f.parentId == this._currentFolder.id);
      }
    }

    files.sort((a, b) => {
      if (this.sortBy === 'name') {
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      } else if (this.sortBy === 'type') {
        if (a.contentType == null) {
          return -1;
        }
        if (b.contentType == null) {
          return 1;
        }
        return a.contentType.toLowerCase().localeCompare(b.contentType.toLowerCase());
      } else if (this.sortBy === 'date') {
        return new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime();
      }
      return 0;
    });
    return files;
  }


  ngOnInit(): void {
    this.ownerId = this.activatedRoute.snapshot.params.ownerId;
    if (this.ownerId == null && this.dialogConfig) {
      this.ownerId = this.dialogConfig.data.ownerId;
      this.selectionOptions = this.dialogConfig.data.options;
      this.enabledSelection = true;
    }
    this.fileStoreService.loadFileStore(this.ownerId, true).subscribe((fileStore) => {
      this.fileStore = fileStore;
      this.unoptimizedVideos = fileStore.files.filter(file => {return !this.isFileReady(file)});
      this.loading = false;
      if(this.unoptimizedVideos.length > 0){
          this.reloadUnoptimizedFiles(this.unoptimizedVideos);
      }
    });
    this.apollo.query<Query>({ query: this.USAGE_STATISTIC_QUERY, variables: {} }).subscribe((result) => {
      this.usageStatistics = result.data.usageStatistics;
    });
  }

  ngOnDestroy() {
      clearTimeout(this.optimizerTimer);
  }

    reloadUnoptimizedFiles(unoptimizedFiles: FsFile[]): void {
      this.optimizerTimer = setTimeout(()=>{
          for(let file of unoptimizedFiles) {
              this.fileStoreService.loadFileStoreFile(this.fileStore.id, file.id).subscribe((result) => {
                  if(result == null){
                      this.unoptimizedVideos.splice(this.unoptimizedVideos.findIndex(optimizedVideoFile => file.id == optimizedVideoFile.id), 1);
                  }
                  if(this.isFileReady(result)){
                      this.unoptimizedVideos.splice(this.unoptimizedVideos.findIndex(file => file.id == result.id), 1);
                      this.fileStore.files[this.fileStore.files.findIndex(file => file.id == result.id)] = result;
                      this.fileStore.files = [...this.fileStore.files];
                  }
              })
          }
          if(this.unoptimizedVideos.length > 0){
              this.reloadUnoptimizedFiles(this.unoptimizedVideos);
          }
      },5000);
  }

  onPrepareContextMenu(file: FsFile, event: any): void {
    event.preventDefault();
    event.stopPropagation();
    this.bottomActionMenuOpen = true;
    this.evalContextMenuOptions(file);
    if (this.searchModeEnabled || this.enabledSelection) {
      return;
    }
    this.selectedContextMenuFile = file;
  }



  /**
  * eval menu
  */
  evalContextMenuOptions(file: FsFile): void {
    this.contextMenuOptions.forEach(option => option.visible = false);
    const downloadOption = this.contextMenuOptions.find(option => option.id === 'download');
    const renameOption = this.contextMenuOptions.find(option => option.id === 'rename');
    const deleteOption = this.contextMenuOptions.find(option => option.id === 'delete');
    const cutOption = this.contextMenuOptions.find(option => option.id === 'cut');
    const pasteOption = this.contextMenuOptions.find(option => option.id === 'paste');

    if (this.fileStore.writeAccess) {
      renameOption.visible = true;
      deleteOption.visible = true;
      cutOption.visible = true;
    }
    if (!file.directory) {
      downloadOption.visible = true;
    }
    if (file.directory) {
      if (this.cuttedFiles?.length > 0) {
        pasteOption.visible = true;
        pasteOption.label = $localize`:@@filestore-browser-menu.paste:Paste (${this.cuttedFiles.length})`;
      }
    }
  }

  /**
   * eval menu
   */
  evalTopLevelMenuOptions(): void {
    this.topLevelMenuOptions.forEach(option => option.visible = false);
    const renameOption = this.topLevelMenuOptions.find(option => option.id === 'rename');
    const deleteOption = this.topLevelMenuOptions.find(option => option.id === 'delete');
    const cutOption = this.topLevelMenuOptions.find(option => option.id === 'cut');
    const pasteOption = this.topLevelMenuOptions.find(option => option.id === 'paste');
    const addDirectory = this.topLevelMenuOptions.find(option => option.id === 'addDirectory');
    const operationLog = this.topLevelMenuOptions.find(option => option.id === 'operation-log');
    const download = this.topLevelMenuOptions.find(option => option.id === 'download');

    if (this.fileStore.writeAccess) {
      if (this.selectedFiles?.length > 0) {
        cutOption.visible = true;
        deleteOption.visible = true;
        if (this.selectedFiles.length == 1) {
          renameOption.visible = true;
        }
        if (this.selectedFiles.filter((f) => f.directory == true).length == 0) {
          download.visible = true;
        }
      }
      else if(this.selectedFiles?.length == 0){
          addDirectory.visible = true;
      }
      if (this.cuttedFiles?.length > 0) {
        pasteOption.visible = true;
        pasteOption.label = $localize`:@@filestore-browser-menu.paste:Paste (${this.cuttedFiles.length})`;
      }
    }
    if (this.fileStore.operationLogAccess == true && this.selectedFiles?.length == 0) {
      operationLog.visible = true;
    }

  }

  /**
   * check downloadable files
   * @returns
   */
  isDownloadableSelection(): boolean {
    if (this.selectedFiles == null || this.selectedFiles.length == 0) {
      return false;
    }
    return this.selectedFiles.filter(f => f.directory == true).length == 0;
  }

  /**
   * opens create directory dialog
   */
  openCreateDirectoryDialog(): void {
    this.dialogControl.createDirectory.visible = true;
  }

  /**
   * create directory
   */
  onCreateDirectory(): void {
    this.dialogControl.createDirectory.visible = false;
    const parentId = this._currentFolder == null ? null : this._currentFolder.id;
    this.fileStoreService.createDirectory(this.fileStore.id, this.dialogControl.createDirectory.name, parentId).subscribe((result) => {
      const message = $localize`:@@filestore-browser.directory-add.success:Directory created`;
      this.messageService.add({ severity: 'success', detail: message, key:'filestoreToast' });
      this.fileStore.files.push(result);
      this.dialogControl.createDirectory.name = null;
    })
  }

  /**
   * change current folder
   * @param folder
   */
  changeFolder(folder: FsFile): void {
    if (folder == null) {
      this._currentFolder = null;
      this.currentFolderHierachy = [];
      this.selectedFiles = [];
      return;
    }
    this._currentFolder = folder;
    this.apollo.query<Query>({
      query: this.QUERY_HIERACHY,
      variables: {
        id: folder.id,
        storeId: this.fileStore.id
      },
    }).pipe(map(r => {
      return r.data.resolveFileHierarchy.map(file => {
        return { title: file.name, id: file.id, data: file } as MenuItem;
      })
    })).subscribe((hierachy) => {
      this.currentFolderHierachy = [...hierachy, { title: folder.name, id: folder.id, data: folder } as MenuItem];
    });
  }

  /**
   *
   * @param file
   */
  onFileDoubleClicked(file: FsFile): void {
    if (this.searchModeEnabled) {
      this.onCloseSearch();
      const target = file.directory == true ? file.id : file.parentId;
      if (target) {
        this.changeFolder(this.fileStore.files.find(file => file.id === target));
      } else {
        this.changeFolder(null);
      }
      return;
    }

    if (file.directory) {
      this.changeFolder(file);
    } else {
      const fileId = file.id;
      if (this.expandedRowKey[fileId]) {
        this.expandedRowKey[fileId] = !this.expandedRowKey[fileId];
      } else {
        this.expandedRowKey[fileId] = true;
      }
    }
  }

  onDownloadFiles(file?: FsFile): void {
    let toDownload: FsFile[] = [];
    if (file) {
      toDownload = [file];
    } else {
      for (const file of this.selectedFiles) {
        if (!file.directory && file.downloadUrl) {
          toDownload.push(file);
        }
      }
    }
    this.coreLibService.downloadFiles(toDownload.map(f => ({ "filename": f.name, "contentType": f.contentType, "url": f.downloadUrl })));
  }
  /**
   * navigates folder up
   */
  onNavigateFolderUp(): void {
    if (this._currentFolder.parentId) {
      this.changeFolder(this.fileStore.files.find((file) => file.id === this._currentFolder.parentId))
    } else {
      this.changeFolder(null);
    }
  }

  /**
   * opens the upload dialog
   */
  openUploadDialog(): void {
    const parentId = this._currentFolder == null ? null : this._currentFolder.id;
    this.dialogService.open(FilestoreUploadWebComponent, {
      header: $localize`:@@filestore-browser.upload-files.header: Upload Files`,
      data: {
        storeId: this.fileStore.id,
        parentId: parentId,
        acceptedFileTypes: this.fileStore.acceptedFileTypes
      },
      height: '90%',
      focusOnShow: false,
      contentStyle: { display: 'flex' }
    }).onClose.subscribe((result) => {
      if (result && result.changes) {
        for (const fsFile of result.changes) {
          const index = this.fileStore.files.findIndex((f) => f.id === fsFile.id);
          if (index > -1) {
            this.fileStore.files[index] = fsFile;
          } else {
            this.fileStore.files.push(fsFile);
          }
          if(!this.isFileReady(fsFile)){
              this.unoptimizedVideos.push(fsFile);
          }
        }
        this.fileStore.files = [...this.fileStore.files];
        this.updateUsageStatistics();
        if(!this.optimizerTimer){
            this.reloadUnoptimizedFiles(this.unoptimizedVideos);
        }
      }
    });
  }

  /**
   * refresh files from server
   */
  refreshFiles(): void {
    this.fileStoreService.loadFileStore(this.ownerId, true).subscribe((fileStore) => {
      this.fileStore.files = fileStore.files;
    });
  }

  /**
   * opens the rename dialog
   */
  openRenameFileDialog(file?: FsFile): void {
    this.dialogControl.rename.visible = true;
    if (file) {
      this.dialogControl.rename.file = { ...file };
    } else {
      this.dialogControl.rename.file = { ...this.selectedFiles[0] };;
    }
    this.dialogControl.rename.fileEnding = this.getFileEnding(this.dialogControl.rename.file.name);
  }

  /**rename completed */
  onRenameFile(): void {
    this.dialogControl.rename.visible = false;
      this.dialogControl.rename.fileEnding = "";
    this.fileStoreService.renameFileStoreFile(this.fileStore.id, this.dialogControl.rename.file.id, this.dialogControl.rename.file.name).subscribe((file) => {
      const index = this.fileStore.files.findIndex((f) => f.id === file.id);
      this.fileStore.files[index] = file;
    })
  }
    /**returns ending/type of file */
    getFileEnding(fileName: string): string{
        return fileName.split(".").pop();
    }

    isFolderName(fileName: string): boolean {
        return fileName.split(".").length == 1;
    }

  /**
   *
   */
  onDeleteFiles(file?: FsFile): void {
    let filesToDelete: FsFile[] = [];
    if (file) {
      filesToDelete.push(file);
    } else {
      filesToDelete = [...this.selectedFiles];
    }
    this.confirmationService.confirm({
      header: $localize`:@@file-store-browser.delete.header:Delete files`,
      message: $localize`:@@file-store-browser.delete.message:Do you really want to delete the ${filesToDelete.length} selected files. Directories are deleted recursively. Please note that this action cannot be undone.`,
      acceptLabel: $localize`:@@file-store-browser.delete.yes:Delete`,
      rejectLabel: $localize`:@@file-store-browser.delete.cancel:Cancel`,
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      acceptIcon: "none",
      rejectIcon: "none",
      defaultFocus: 'none',
      accept: () => {
        this.fileStoreService.deleteFiles(this.fileStore.id, filesToDelete.map(f => f.id)).subscribe((deletedCount) => {
          const message = $localize`:@@filestore-browser.delete.success:${deletedCount} files were deleted`;
          this.messageService.add({ severity: 'success', detail: message, key:'filestoreToast' });
          this.selectedFiles = [];
          this.refreshFiles();
          this.updateUsageStatistics();
        });
      }
    });
  }

  /**
   * updates the stats
   */
  updateUsageStatistics(): void {
    this.apollo.mutate<Mutation>({
      mutation: this.USAGE_STATISTIC_MUTATION,
      variables: {}
    }).subscribe(result => {
      this.usageStatistics = result.data.updateStatistics;
    });
  }

  canAddFiles(): boolean {
    if (this.fileStore?.writeAccess && (this.selectedFiles == null || this.selectedFiles.length == 0)) {
      if (this.usageStatistics?.storageUsedPercentage < 100) {
        return true;
      }
    }

    return false;
  }
  getUsagePercentage(): number {
    return Math.round(this.usageStatistics.storageUsedPercentage);
  }

  onCutFiles(file?: FsFile): void {
    if (file) {
      this.cuttedFiles = [file];
    } else {
      this.cuttedFiles = [...this.selectedFiles];
    }
    this.pasteButtonLabel = $localize`:@@filestore-browser.button.paste.label:Paste ${this.cuttedFiles.length} files`;

    const message = $localize`:@@filestore-browser.cut-info:You can now paste the files to a new path`;
    this.messageService.add({ severity: 'info', detail: message, key:'filestoreToast'  });
  }

  onPasteFiles(target: FsFile): void {
    let parentId = target ? target.id : null;
    this.fileStoreService.moveFiles(this.fileStore.id, this.cuttedFiles.map(f => f.id), parentId).subscribe(modifiedFiles => {
      for (const modFile of modifiedFiles) {
        const index = this.fileStore.files.findIndex((f) => f.id === modFile.id);
        this.fileStore.files[index] = modFile;
      }
      const message = $localize`:@@filestore-browser.move.success:Files moved`;
      this.messageService.add({ severity: 'success', detail: message, key:'filestoreToast'  });
      this.selectedFiles = [];
      this.cuttedFiles = [];
    });
  }

  /**
   * opens operation dialog
   */
  onOpenOperationLog(): void {
    this.dialogService.open(FilestoreOperationLogComponent, {
      header: $localize`:@@filestore-browser.operation-log.title: Operation Log`,
      data: {
        storeId: this.fileStore.id
      },
      width: '90%',
      height: '90%',
      focusOnShow: false,
      contentStyle: { display: 'flex' }
    });
  }


  /**
   * turn on / off selection
   */
  onToggleSelection(): void {
    this.enabledSelection = !this.enabledSelection;
    if (!this.enabledSelection) {
      this.selectedFiles = [];
    }
  }

  getFileIcon(contentType: string) {
    return this.fileStoreUtils.getFileIcon(contentType);
  }

  onOpenSearch(): void {
    this.searchModeEnabled = true;
    this.selectedFiles = [];
    this.cuttedFiles = [];
    this.enabledSelection = false;
  }
  onCloseSearch(): void {
    this.searchModeEnabled = false;
    this.table.clear();
  }

  validateFileSelection(): string {
    if (this.selectedFiles == null) {
      return "No files selected";
    }
    if (this.selectedFiles.length > this.selectionOptions.limit) {
      return $localize`:@@filestore-browser-selection-invalid-limit:You can only select ${this.selectionOptions.limit} files`;
    }

    const invalidFiles = this.selectedFiles.some(file => {
      const fileType = file.contentType.split('/')[0];
      return !this.selectionOptions.mimeTypes.includes(fileType);
    });

    if (invalidFiles) {
      return $localize`:@@filestore-browser-selection-invalid-file-types:You have made an invalid selection of files in this context.`;
    }

    return null;
  }

  onAcceptSelection(): void {
    this.dialogRef.close(this.selectedFiles);
  }

  onSelcectionChange(): void{
      this.topLevelActionMenu.hide();
  }

  isFileReady(file: FsFile): boolean{
      if(file.contentType != null && file.contentType.includes('video')){
          if(file.optimizedUrl && file.optimizedUrl.length){
              return true;
          }
          return false;
      }
      else{
          return true;
      }
  }
}
